import React, { useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { useKeycloak } from '@react-keycloak/web';
import { komprimiereBild } from '../bildKompressor';
import { uploadDokument } from './ApiClient';

/**
 * Dokument nachreichen: bewusst IMMER verfügbar — unabhängig von den
 * Zeitfenstern der Antrags-Kacheln und ohne die Vollständigkeits-Prüfungen
 * der Formulare. Ein neues gültiges Dokument zu hinterlegen ist nie falsch.
 * Der Upload landet in der Prüf-Queue des Gateways (pending_dokument) und
 * wird erst nach Admin-Abgleich (MRZ-Regeln) ins USP übernommen.
 */
function DokumentNachreichen({ goBack }: { goBack: () => void }) {
  const { keycloak } = useKeycloak();
  const [art, setArt] = useState('Reisepass');
  const [gueltigBis, setGueltigBis] = useState('');
  const [dateien, setDateien] = useState<File[]>([]);
  const [dateiInfo, setDateiInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [hochgeladen, setHochgeladen] = useState(0);
  const [ergebnis, setErgebnis] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);

  const istAusweis = art === 'Reisepass' || art === 'Personalausweis';

  const handleDatei = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const roh = Array.from(e.target.files || []);
    setDateiInfo('');
    setDateien([]);
    if (roh.length === 0) return;
    const fertig: File[] = [];
    const hinweise: string[] = [];
    for (const f of roh) {
      const r = await komprimiereBild(f);
      if (r.problem) {
        hinweise.push(`⚠️ ${f.name}: ${r.problem}`);
        continue;
      }
      if (r.verkleinert) {
        hinweise.push(`✅ ${f.name} automatisch verkleinert: ${(f.size / 1e6).toFixed(1)} MB → ${(r.file.size / 1e6).toFixed(1)} MB`);
      }
      fertig.push(r.file);
    }
    setDateiInfo(hinweise.join(' · '));
    setDateien(fertig);
  };

  /** Nach einem erfolgreichen Upload direkt das nächste Dokument (z.B. erst
   * der neue Ausweis, dann der Strafregisterauszug) hochladen können. */
  const nochEins = () => {
    setErgebnis(null);
    setDateien([]);
    setDateiInfo('');
    setGueltigBis('');
  };

  const absenden = async () => {
    if (dateien.length === 0) return;
    setBusy(true);
    setErgebnis(null);
    try {
      const note = `${art} nachgereicht` + (gueltigBis ? `, gültig bis ${gueltigBis}` : '');
      for (const [i, f] of dateien.entries()) {
        const teil = dateien.length > 1 ? ` (Datei ${i + 1}/${dateien.length})` : '';
        await uploadDokument(keycloak.token, f, note + teil);
      }
      setHochgeladen((n) => n + dateien.length);
      setErgebnis({
        variant: 'success',
        text: (dateien.length > 1
          ? `Danke! Deine ${dateien.length} Dateien sind bei uns eingegangen. `
          : 'Danke! Dein Dokument ist bei uns eingegangen. ')
          + 'Wir gleichen alles mit deinen Daten ab und hinterlegen es im Behördensystem — '
          + 'du brauchst nichts weiter zu tun. Bei Rückfragen melden wir uns.',
      });
    } catch (err) {
      setErgebnis({ variant: 'danger', text: 'Hochladen fehlgeschlagen: ' + (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container fluid='md' className='py-4' style={{ maxWidth: 720 }}>
      <Button variant='link' className='px-0 mb-2' onClick={goBack}>← Zurück</Button>
      <h2 className='mb-3'>Dokument nachreichen</h2>

      <Card className='mb-4'>
        <Card.Body>
          <Card.Title as='h5'>Wofür diese Seite da ist</Card.Title>
          <p className='mb-2' style={{ fontSize: '0.95rem' }}>
            Du hast einen neuen Reisepass oder Personalausweis — oder einen frischen
            Strafregisterauszug — und möchtest ihn einfach nur bei uns hinterlegen?
            Dann bist du hier richtig: <strong>jederzeit möglich</strong>, ganz ohne
            Antragsformular und unabhängig davon, ob deine ZÜP-Verlängerung schon fällig ist.
          </p>
          <div className='text-muted' style={{ fontSize: '0.85rem' }}>
            Wir prüfen dein Dokument und übernehmen es in deine ZÜP-Akte. Läuft gerade ein
            Antrag, fließt es dort automatisch ein.
          </div>
        </Card.Body>
      </Card>

      {ergebnis && <Alert variant={ergebnis.variant}>{ergebnis.text}</Alert>}

      {ergebnis?.variant === 'success' && (
        <Button variant='outline-primary' onClick={nochEins}>
          Weiteres Dokument hochladen (z.&nbsp;B. Strafregisterauszug oder Rückseite)
        </Button>
      )}

      {ergebnis?.variant !== 'success' && (
        <Form>
          <Form.Group className='mb-3'>
            <Form.Label>Was möchtest du hochladen?</Form.Label>
            <Form.Select value={art} onChange={(e) => setArt(e.target.value)}>
              <option>Reisepass</option>
              <option>Personalausweis</option>
              <option>Strafregisterauszug / Führungszeugnis</option>
              <option>Sonstiges Dokument</option>
            </Form.Select>
          </Form.Group>
          {istAusweis && (
            <Form.Group className='mb-3'>
              <Form.Label>Gültig bis (steht auf dem Dokument)</Form.Label>
              <Form.Control
                type='date'
                value={gueltigBis}
                onChange={(e) => setGueltigBis(e.target.value)}
                style={{ maxWidth: 220 }}
              />
            </Form.Group>
          )}
          <Form.Group className='mb-2'>
            <Form.Label>
              {istAusweis
                ? 'Scan(s) oder Foto(s) — bei Personalausweis bitte Vorder- UND Rückseite (mehrere Dateien möglich)'
                : 'Scan oder Foto (gut lesbar)'}
            </Form.Label>
            <Form.Control type='file' multiple accept='image/*,application/pdf' onChange={handleDatei} />
            {dateien.length > 0 && (
              <Form.Text className='text-muted d-block'>
                {dateien.length} Datei(en) ausgewählt: {dateien.map((f) => f.name).join(', ')}
              </Form.Text>
            )}
            {dateiInfo && <Form.Text className={dateiInfo.startsWith('⚠️') ? 'text-danger' : 'text-success'}>{dateiInfo}</Form.Text>}
          </Form.Group>
          <Button className='mt-2' disabled={busy || dateien.length === 0} onClick={absenden}>
            {busy ? <Spinner size='sm' animation='border' />
              : dateien.length > 1 ? `${dateien.length} Dateien hochladen` : 'Dokument hochladen'}
          </Button>
          {hochgeladen > 0 && (
            <Form.Text className='text-muted d-block mt-2'>
              In dieser Sitzung bereits hochgeladen: {hochgeladen} Datei(en).
            </Form.Text>
          )}
        </Form>
      )}
    </Container>
  );
}

export default DokumentNachreichen;
